import { useCallback, useMemo } from "react";

import { useFormContext } from "react-hook-form";

import { Delete as DeleteIcon } from "@mui/icons-material";
import { IconButton, Switch } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { formatMoney, formatNumber } from "@/features/shared";
import { ProductVariantNameCell, useSnackbar } from "@/features/ui";
import { hasPricing } from "../../sales-return-rules";
import { StockInModeEnum } from "../../stock-in-mode";

export const ProductVariantsSection = () => {
  const { showSnackbar } = useSnackbar();
  const { watch, setValue } = useFormContext();
  const returnableProductVariants = watch("returnableProductVariants");
  const salesReturnExpenses = watch("salesReturnExpenses");
  const returnableType = watch("returnableType");
  const stockInMode = watch("stockInMode");

  const handleDelete = useCallback(
    (id) => {
      const updatedVariants = returnableProductVariants.filter(
        (variant) => variant.id !== id
      );
      setValue("returnableProductVariants", updatedVariants);

      // Unpin the source once nothing references it any more. The type stays
      // as chosen: clearing it would leave the form with no source selected,
      // and an empty type is how the payload spells "non-invoice return".
      if (updatedVariants.length === 0 && salesReturnExpenses.length === 0) {
        setValue("returnableId", null);
        setValue("returnableReferenceNumber", null);
      }
    },
    [returnableProductVariants, salesReturnExpenses, setValue]
  );

  const handleStockInModeToggle = useCallback(
    (variantId, currentStockInMode) => {
      const updatedVariants = returnableProductVariants.map((variant) => {
        if (variant.id === variantId) {
          const newStockInMode =
            currentStockInMode === StockInModeEnum.FULL
              ? StockInModeEnum.NONE
              : StockInModeEnum.FULL;
          return { ...variant, stockInMode: newStockInMode };
        }
        return variant;
      });
      setValue("returnableProductVariants", updatedVariants);
    },
    [returnableProductVariants, setValue]
  );

  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      // Clearing a numeric cell commits null (MUI's numeric valueParser), which
      // slips past the guards below and reaches the payload. Coerce to 0 first.
      const row = {
        ...newRow,
        quantity: Number(newRow.quantity) || 0,
        price: Number(newRow.price) || 0,
        discount: Number(newRow.discount) || 0,
      };
      const subtotal = row.price * row.quantity;

      // Validate quantity is at least 1
      if (row.quantity < 1) {
        showSnackbar("Kuantitas tidak boleh kurang dari 1", "error");
        return oldRow; // Revert changes
      }

      // Validate quantity doesn't exceed maxQuantity
      if (row.maxQuantity && row.quantity > row.maxQuantity) {
        showSnackbar(
          `Kuantitas tidak boleh lebih dari ${row.maxQuantity} (kuantitas tersedia dari surat jalan)`,
          "error"
        );
        return oldRow; // Revert changes
      }

      // Validate discount doesn't exceed subtotal
      if (row.discount > subtotal) {
        showSnackbar(
          `Diskon tidak boleh melebihi total harga (${formatMoney(subtotal)})`,
          "error"
        );
        return oldRow; // Revert changes
      }

      // Update the row in the form state
      const updatedVariants = returnableProductVariants.map((variant) =>
        variant.id === row.id ? row : variant
      );
      setValue("returnableProductVariants", updatedVariants);

      return row;
    },
    [returnableProductVariants, setValue, showSnackbar]
  );

  const showPricing = hasPricing(returnableType);

  // DataGrid freezes whatever array it gets as `rows`; give it a copy so the
  // form keeps ownership of its own state.
  const rows = useMemo(
    () => [...returnableProductVariants],
    [returnableProductVariants]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "name",
        headerName: "Nama Barang",
        flex: 3,
        renderCell: ({ row: { name, sku } }) => (
          <ProductVariantNameCell name={name} sku={sku} />
        ),
      },
      {
        field: "quantity",
        headerName: "Kuantitas",
        type: "number",
        valueFormatter: (value) => formatNumber(value),
        flex: 1,
        editable: true,
      },
      {
        field: "unitOfMeasurement",
        headerName: "Satuan",
        flex: 1,
      },
    ];

    const pricingColumns = showPricing
      ? [
          {
            field: "price",
            headerName: "@Harga",
            type: "number",
            flex: 1,
            editable: true,
            valueFormatter: (value) => formatNumber(value || 0),
          },
          {
            field: "discount",
            headerName: "Diskon",
            type: "number",
            flex: 1,
            editable: true,
            valueFormatter: (value) => formatNumber(value || 0),
          },
          {
            field: "totalPrice",
            headerName: "Total Harga",
            type: "number",
            flex: 1,
            valueGetter: (_, row) => {
              const subtotal = (row.price || 0) * (row.quantity || 0);
              return subtotal - (row.discount || 0);
            },
            valueFormatter: (value) => formatNumber(value || 0),
          },
        ]
      : [];

    const stockInModeColumn = {
      field: "stockInMode",
      headerName: "Masuk Stok",
      flex: 1,
      renderCell: ({ row }) => {
        const isDisabled =
          stockInMode === StockInModeEnum.FULL ||
          stockInMode === StockInModeEnum.NONE;
        const isChecked = row.stockInMode === StockInModeEnum.FULL;

        return (
          <Switch
            checked={isChecked}
            disabled={isDisabled}
            onChange={() => handleStockInModeToggle(row.id, row.stockInMode)}
            size="small"
          />
        );
      },
    };

    const actionColumn = {
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      getActions: ({ row }) => [
        <IconButton
          key="delete"
          onClick={() => handleDelete(row.id)}
          size="small"
          color="error"
        >
          <DeleteIcon />
        </IconButton>,
      ],
    };

    return [
      ...baseColumns,
      ...pricingColumns,
      stockInModeColumn,
      actionColumn,
    ];
  }, [
    showPricing,
    stockInMode,
    handleDelete,
    handleStockInModeToggle,
  ]);

  return (
    <DataGrid
      getRowHeight={() => "auto"}
      rows={rows}
      columns={columns}
      disableRowSelectionOnClick
      hideFooter
      processRowUpdate={handleProcessRowUpdate}
      onProcessRowUpdateError={(error) => showSnackbar(error.message, "error")}
    />
  );
};
